import {
  Calendar,
  Clock,
  Database,
  FileText,
  Filter,
  GitBranch,
  type LucideIcon,
  Mail,
  MessageSquare,
  ShoppingCart,
  Users,
  Webhook,
} from "lucide-react";

export interface Template {
  id: string;
  category:
    | "ops"
    | "founders"
    | "developers"
    | "sales"
    | "marketing"
    | "support";
  name: string;
  outcome: string;
  nodes: { icon: LucideIcon; label: string }[];
  description: string;
  whenToUse: string;
  uses?: string;
  time?: string;
  popular?: boolean;
}

export const templates: Template[] = [
  {
    id: "lead-capture",
    category: "sales",
    name: "Lead Capture Pipeline",
    outcome: "Route leads to CRM and notify sales instantly",
    nodes: [
      { icon: Webhook, label: "Form" },
      { icon: Database, label: "Enrich" },
      { icon: Mail, label: "Notify" },
    ],
    description:
      "Automatically captures form submissions, enriches contact data with company info, creates CRM records, and pings your sales team in Slack.",
    whenToUse: "When you need leads to reach sales within seconds, not hours.",
    uses: "2.4k",
    time: "5 min",
    popular: true,
  },
  {
    id: "billing-alerts",
    category: "founders",
    name: "Billing Event Alerts",
    outcome: "Never miss a payment event-success or failure",
    nodes: [
      { icon: Webhook, label: "Stripe" },
      { icon: Filter, label: "Filter" },
      { icon: Mail, label: "Alert" },
    ],
    description:
      "Listens to Stripe webhooks for payments, failures, and subscription changes, then routes alerts to the right channels.",
    whenToUse: "When you need real-time visibility into revenue events.",
    uses: "1.8k",
    time: "8 min",
    popular: true,
  },
  {
    id: "webhook-router",
    category: "developers",
    name: "Webhook Router",
    outcome: "Fan out webhooks to multiple destinations",
    nodes: [
      { icon: Webhook, label: "Receive" },
      { icon: Filter, label: "Route" },
      { icon: Database, label: "Deliver" },
    ],
    description:
      "Receives webhooks from any source, applies conditional logic, and routes payloads to different endpoints based on content.",
    whenToUse:
      "When you need to fan out webhooks without writing custom routing code.",
    uses: "3.1k",
    time: "10 min",
    popular: false,
  },
  {
    id: "scheduled-reports",
    category: "ops",
    name: "Scheduled Reports",
    outcome: "Automated reports delivered on schedule",
    nodes: [
      { icon: Clock, label: "Schedule" },
      { icon: Database, label: "Query" },
      { icon: Mail, label: "Send" },
    ],
    description:
      "Runs on a schedule to pull metrics from your data sources, compiles them into a report, and delivers to Slack or email.",
    whenToUse: "When you want daily/weekly summaries without manual work.",
    uses: "1.2k",
    time: "12 min",
    popular: false,
  },
  {
    id: "user-onboarding",
    category: "founders",
    name: "Onboarding Sequence",
    outcome: "Guide new users automatically",
    nodes: [
      { icon: Webhook, label: "Signup" },
      { icon: Filter, label: "Check" },
      { icon: Mail, label: "Email" },
    ],
    description:
      "Monitors new user progress and sends contextual tips, reminders, and encouragement based on their actual usage.",
    whenToUse: "When you want every new user to get a personalized experience.",
    uses: "2.9k",
    time: "15 min",
    popular: true,
  },
  {
    id: "error-alerting",
    category: "developers",
    name: "Error Alerting",
    outcome: "Get instant alerts when things break",
    nodes: [
      { icon: Webhook, label: "Error" },
      { icon: Filter, label: "Dedupe" },
      { icon: Mail, label: "Alert" },
    ],
    description:
      "Aggregates errors from monitoring tools, deduplicates noise, and sends actionable alerts to Slack or PagerDuty.",
    whenToUse:
      "When you want to catch production issues before users report them.",
    uses: "1.5k",
    time: "7 min",
    popular: false,
  },
  {
    id: "email-campaign",
    category: "marketing",
    name: "Email Campaign Automation",
    outcome: "Trigger personalized email sequences based on behavior",
    nodes: [
      { icon: Users, label: "Segment" },
      { icon: Filter, label: "Logic" },
      { icon: Mail, label: "Send" },
    ],
    description:
      "Trigger personalized email sequences based on user behavior and engagement patterns.",
    whenToUse:
      "When you want to nurture leads with automated, personalized outreach.",
    uses: "1.8k",
    time: "8 min",
    popular: true,
  },
  {
    id: "database-sync",
    category: "developers",
    name: "Database Sync Workflow",
    outcome: "Keep databases in sync with real-time flows",
    nodes: [
      { icon: Database, label: "Source" },
      { icon: Filter, label: "Transform" },
      { icon: Database, label: "Target" },
    ],
    description:
      "Keep your databases in sync with real-time bi-directional data flows.",
    whenToUse: "When you need consistent data across multiple systems.",
    uses: "3.1k",
    time: "10 min",
    popular: false,
  },
  {
    id: "invoice-processing",
    category: "ops",
    name: "Invoice Processing",
    outcome: "Extract, validate, and push to accounting",
    nodes: [
      { icon: FileText, label: "Extract" },
      { icon: Filter, label: "Validate" },
      { icon: Database, label: "Push" },
    ],
    description:
      "Extract data from invoices, validate, and push to your accounting system.",
    whenToUse: "When you want to automate manual invoice entry.",
    uses: "1.2k",
    time: "12 min",
    popular: false,
  },
  {
    id: "ecommerce-orders",
    category: "ops",
    name: "E-commerce Order Flow",
    outcome: "Process orders and notify customers automatically",
    nodes: [
      { icon: ShoppingCart, label: "Order" },
      { icon: Database, label: "Update" },
      { icon: Mail, label: "Notify" },
    ],
    description:
      "Process orders, update inventory, and notify customers automatically.",
    whenToUse: "When you need seamless order-to-delivery automation.",
    uses: "2.9k",
    time: "15 min",
    popular: true,
  },
  {
    id: "support-routing",
    category: "support",
    name: "Support Ticket Routing",
    outcome: "Route tickets to the right team with priority scoring",
    nodes: [
      { icon: MessageSquare, label: "Ticket" },
      { icon: Filter, label: "Score" },
      { icon: Users, label: "Assign" },
    ],
    description:
      "Analyze incoming tickets and route to the right team with priority scoring.",
    whenToUse: "When you want faster response times through smart routing.",
    uses: "1.5k",
    time: "7 min",
    popular: false,
  },
  {
    id: "meeting-scheduler",
    category: "ops",
    name: "Meeting Scheduler",
    outcome: "Automate scheduling with calendar integration",
    nodes: [
      { icon: Calendar, label: "Request" },
      { icon: Filter, label: "Check" },
      { icon: Mail, label: "Confirm" },
    ],
    description:
      "Automate meeting scheduling with calendar integration and reminders.",
    whenToUse: "When you want to eliminate scheduling back-and-forth.",
    uses: "2.1k",
    time: "6 min",
    popular: false,
  },
  {
    id: "cicd-notifications",
    category: "developers",
    name: "CI/CD Notifications",
    outcome: "Get notified on deployment success or failure",
    nodes: [
      { icon: GitBranch, label: "Deploy" },
      { icon: Filter, label: "Check" },
      { icon: Mail, label: "Alert" },
    ],
    description:
      "Get notified on Slack when deployments succeed, fail, or need review.",
    whenToUse:
      "When you need real-time visibility into your deployment pipeline.",
    uses: "1.9k",
    time: "4 min",
    popular: false,
  },
];

export const templateCategories = [
  { id: "all", label: "All" },
  { id: "sales", label: "Sales" },
  { id: "marketing", label: "Marketing" },
  { id: "ops", label: "Operations" },
  { id: "developers", label: "Developer" },
  { id: "support", label: "Support" },
  { id: "founders", label: "Founders" },
];

export const getCategoryStyles = (category: string) => {
  switch (category) {
    case "ops":
      return "bg-primary/10 text-primary border-primary/20";
    case "founders":
      return "bg-success/10 text-success border-success/20";
    case "developers":
      return "bg-warning/10 text-warning border-warning/20";
    case "sales":
      return "bg-primary/10 text-primary border-primary/20";
    case "marketing":
      return "bg-accent/10 text-accent border-accent/20";
    case "support":
      return "bg-muted text-muted-foreground border-border";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};
